import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Trash2, Sparkles, Play, Target as TargetIcon } from "lucide-react";
import { formatNum } from "../lib/utils";
import { toast } from "sonner";
import { WidgetLibrary } from "../components/widgets/WidgetLibrary";

const TOKEN_HELP = [
  { token: "SUM_SCOPE(1|2|3)", desc: "Sum of co2e_tonnes for a scope (current FY)" },
  { token: "SUM_CATEGORY('Purchased Electricity')", desc: "Sum of value for a category" },
  { token: "SUM_ACTIVITY('Electricity — Nursery')", desc: "Sum of value for activities starting with that text" },
  { token: "SUM_S3CAT(2)", desc: "Sum of tCO2e for a Scope 3 category number" },
  { token: "KPI('SOC-HC')", desc: "Latest value of Social/Governance KPI" },
  { token: "CONST(720)", desc: "Literal number" },
];

export default function CustomKPIs() {
  const [kpis, setKpis] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", unit: "", formula: "", description: "", target_value: "", direction: "lower_better" });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "library" ? "library" : "kpis";
  const setTab = (t) => setParams(t === "library" ? { tab: "library" } : {});

  const load = async () => {
    const { data } = await api.get("/custom-kpis/evaluate-all/2024");
    setKpis(data.kpis);
  };
  useEffect(() => { load(); }, []);

  const testFormula = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/custom-kpis/test", { formula: form.formula, year: 2024 });
      setTestResult({ ok: true, ...data });
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Test failed" });
    } finally { setTesting(false); }
  };

  const save = async () => {
    try {
      const payload = { ...form, target_value: form.target_value ? parseFloat(form.target_value) : null };
      await api.post("/custom-kpis", payload);
      toast.success("Custom KPI created");
      setOpen(false);
      setForm({ code: "", name: "", unit: "", formula: "", description: "", target_value: "", direction: "lower_better" });
      setTestResult(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this KPI?")) return;
    await api.delete(`/custom-kpis/${id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <div className="label-eyebrow mb-1">Control Tower · Custom KPIs &amp; Widget Library</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Custom KPIs</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
          Define tenant-specific metrics with a safe formula language, and curate the widgets shown on your Control Tower homepage.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="kpis" data-testid="ckpi-tab-kpis">Custom KPIs</TabsTrigger>
          <TabsTrigger value="library" data-testid="ckpi-tab-library">Widget Library</TabsTrigger>
        </TabsList>

        <TabsContent value="kpis" className="mt-4 space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Your formula-built metrics. Each can be added to the Control Tower from the Widget Library.</p>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button variant="primary" data-testid="add-kpi-btn"><Plus className="h-4 w-4" /> New KPI</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Define a custom KPI</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} placeholder="GHG-INTENSITY-REV" data-testid="kpi-code-input" /></div>
              <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} placeholder="tCO2e / M-SGD" data-testid="kpi-unit-input" /></div>
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="GHG Intensity per Revenue" data-testid="kpi-name-input" /></div>
              <div className="col-span-2">
                <Label>Formula</Label>
                <Textarea value={form.formula} onChange={(e) => setForm({...form, formula: e.target.value})} placeholder="(SUM_SCOPE(1) + SUM_SCOPE(2)) / CONST(480)" rows={3} className="font-mono text-sm" data-testid="kpi-formula-input" />
              </div>
              <div className="col-span-2"><Label>Target (optional)</Label><Input type="number" step="any" value={form.target_value} onChange={(e) => setForm({...form, target_value: e.target.value})} placeholder="e.g. 12" data-testid="kpi-target-input" /></div>
              <div className="col-span-2"><Label>Direction (for status vs target)</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger data-testid="kpi-direction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lower_better">Lower is better (e.g. intensity, fuel, waste)</SelectItem>
                    <SelectItem value="higher_better">Higher is better (e.g. diversion, coverage, renewable)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} /></div>
            </div>

            <div className="card-flat p-3 bg-surface">
              <div className="label-eyebrow mb-2">Available tokens</div>
              <div className="space-y-1 text-xs font-mono">
                {TOKEN_HELP.map((t) => (
                  <div key={t.token} className="flex gap-3"><span className="text-accent w-56 shrink-0">{t.token}</span><span className="text-muted-foreground">{t.desc}</span></div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={testFormula} disabled={!form.formula || testing} className="flex-1" data-testid="test-formula-btn"><Play className="h-3.5 w-3.5" /> {testing ? "Testing…" : "Test formula"}</Button>
              <Button variant="primary" onClick={save} disabled={!form.code || !form.name || !form.unit || !form.formula} className="flex-1" data-testid="save-kpi-btn">Save KPI</Button>
            </div>

            {testResult && (
              <div className={`card-flat p-3 ${testResult.ok ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"}`} data-testid="test-result">
                {testResult.ok ? (
                  <>
                    <div className="font-display font-bold text-2xl">{formatNum(testResult.value, {decimals: 4})}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Test result · {form.unit}</div>
                    <div className="text-xs font-mono mt-2 text-muted-foreground">
                      Resolved: <span className="text-foreground">{testResult.resolved_expression}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-red-400 font-mono">{testResult.error}</div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {kpis.map((k) => {
          const dir = k.direction || "lower_better";
          const onTrack = k.target_value !== null && k.target_value !== undefined && k.computed_value !== null
            ? (k.code.startsWith("S3") ? null : (dir === "higher_better" ? k.computed_value >= k.target_value : k.computed_value <= k.target_value)) : null;
          return (
            <div key={k.id} className="card-flat p-5" data-testid={`custom-kpi-${k.code}`}>
              <div className="flex items-start justify-between mb-3">
                <Badge variant="outline" className="text-[10px]">{k.code}</Badge>
                <Button variant="ghost" size="icon" onClick={() => remove(k.id)} data-testid="delete-kpi-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
              </div>
              <h4 className="font-display font-semibold leading-tight mb-1">{k.name}</h4>
              <div className="text-xs text-muted-foreground mb-3">{k.description || "—"}</div>
              {k.error ? (
                <div className="text-xs text-red-400 font-mono">⚠ {k.error}</div>
              ) : (
                <div className="font-display font-bold text-3xl">
                  {formatNum(k.computed_value, {decimals: 2})}
                  <span className="text-sm text-muted-foreground font-sans font-normal ml-1">{k.unit}</span>
                </div>
              )}
              {k.target_value !== null && k.target_value !== undefined && (
                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs font-mono">
                  <span className="text-muted-foreground flex items-center gap-1"><TargetIcon className="h-3 w-3" /> Target: {k.target_value} {k.unit}</span>
                  {onTrack !== null && (
                    <Badge variant={onTrack ? "success" : "warning"} data-testid={`kpi-status-${k.code}`}>{onTrack ? "On track" : dir === "higher_better" ? "Below target" : "Above target"}</Badge>
                  )}
                </div>
              )}
              <details className="mt-3">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">Formula</summary>
                <code className="block bg-surface p-2 mt-2 rounded-sm text-[11px] font-mono text-foreground/80 break-all">{k.formula}</code>
              </details>
            </div>
          );
        })}
        {kpis.length === 0 && <div className="md:col-span-3 card-flat p-12 text-center text-muted-foreground"><Sparkles className="h-8 w-8 mx-auto mb-3 opacity-50" />No custom KPIs yet — define your first one.</div>}
          </div>
        </TabsContent>

        <TabsContent value="library" className="mt-4">
          <WidgetLibrary />
        </TabsContent>
      </Tabs>
    </div>
  );
}
